import { jsPDF } from 'jspdf';
import { formatDate } from './dates';

/** Helvetica in jsPDF cannot render ₹ — use ASCII "Rs" for PDF amounts. */
function money(n) {
  const value = Number(n);
  const amount = Number.isFinite(value) ? value : 0;
  const formatted = amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return `Rs ${formatted}`;
}

function drawTable(doc, {
  startX,
  startY,
  widths,
  rows,
  aligns = [],
  header = false,
  fontSize = 8,
  pageBottom = 190,
  margin = 12
}) {
  const padX = 1.5;
  const lineH = 3.4;
  const minRowH = 6;
  let y = startY;

  const ensureSpace = (needed) => {
    if (y + needed <= pageBottom) return;
    doc.addPage();
    y = margin;
  };

  rows.forEach((cells, rowIndex) => {
    const isHeader = header && rowIndex === 0;
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isHeader ? 'bold' : 'normal');

    const wrapped = cells.map((cell, i) => {
      const maxW = Math.max(widths[i] - padX * 2, 4);
      return doc.splitTextToSize(String(cell ?? ''), maxW);
    });
    const rowH = Math.max(minRowH, ...wrapped.map((lines) => lines.length * lineH + 2.5));
    ensureSpace(rowH);

    let x = startX;
    if (isHeader) {
      doc.setFillColor(17, 24, 39);
      doc.rect(startX, y, widths.reduce((a, b) => a + b, 0), rowH, 'F');
      doc.setTextColor(255, 255, 255);
    } else {
      doc.setTextColor(17, 24, 39);
    }

    cells.forEach((_, i) => {
      const w = widths[i];
      doc.setDrawColor(156, 163, 175);
      doc.setLineWidth(0.2);
      doc.rect(x, y, w, rowH, 'S');

      const lines = wrapped[i];
      const align = aligns[i] || 'left';
      const textY = y + 4;
      if (align === 'right') {
        doc.text(lines, x + w - padX, textY, { align: 'right' });
      } else if (align === 'center') {
        doc.text(lines, x + w / 2, textY, { align: 'center' });
      } else {
        doc.text(lines, x + padX, textY);
      }
      x += w;
    });

    doc.setTextColor(17, 24, 39);
    y += rowH;
  });

  return y;
}

function createInvoicePdfDoc(invoice, customer = null) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [148, 210] });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 12;
  let y = margin;

  const company = invoice?.client_name || 'Inventory Management System';
  const customerName = customer?.name || invoice?.customer_name || 'Customer';
  const isGst =
    Number(invoice?.tax_amount) > 0 ||
    Number(invoice?.cgst_amount) > 0 ||
    Number(invoice?.sgst_amount) > 0;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(String(company), margin, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  if (invoice?.client_address) {
    const addr = doc.splitTextToSize(String(invoice.client_address), pageW - margin * 2);
    doc.text(addr, margin, y);
    y += addr.length * 3.5;
  }
  const contacts = [
    invoice?.client_phone ? `Phone: ${invoice.client_phone}` : '',
    invoice?.client_email ? `Email: ${invoice.client_email}` : ''
  ].filter(Boolean).join(' · ');
  if (contacts) {
    doc.text(contacts, margin, y);
    y += 4;
  }
  if (isGst && (invoice?.client_gst_number || '').trim()) {
    doc.text(`GSTIN: ${String(invoice.client_gst_number).trim()}`, margin, y);
    y += 4;
  }

  y += 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('INVOICE', pageW - margin, margin, { align: 'right' });
  doc.setFontSize(10);
  doc.text(String(invoice?.invoice_number || ''), pageW - margin, margin + 5, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(`Date: ${formatDate(invoice?.issue_date)}`, pageW - margin, margin + 10, { align: 'right' });
  const dueLabel = formatDate(invoice?.due_date, '');
  if (dueLabel) {
    doc.text(`Due: ${dueLabel}`, pageW - margin, margin + 14, { align: 'right' });
  }
  doc.text(`Status: ${invoice?.status || '-'}`, pageW - margin, margin + 18, { align: 'right' });

  y = Math.max(y, margin + 24);
  doc.setDrawColor(200);
  doc.line(margin, y, pageW - margin, y);
  y += 5;

  // Bill To as a 2-column table
  const billLines = [String(customerName)];
  if (customer?.address) billLines.push(String(customer.address));
  if (customer?.phone) billLines.push(`Phone: ${customer.phone}`);
  if (customer?.tax_id) billLines.push(`Tax ID: ${customer.tax_id}`);
  y = drawTable(doc, {
    startX: margin,
    startY: y,
    widths: [28, pageW - margin * 2 - 28],
    aligns: ['left', 'left'],
    header: false,
    fontSize: 8,
    margin,
    rows: [['Bill To', billLines.join('\n')]]
  });
  y += 4;

  const tableW = pageW - margin * 2;
  const items = invoice?.items || [];
  const showItemDiscount = items.some((item) => Number(item.discount) > 0);
  const colW = showItemDiscount
    ? [
        tableW * 0.08,
        tableW * 0.38,
        tableW * 0.10,
        tableW * 0.16,
        tableW * 0.12,
        tableW * 0.16
      ]
    : [
        tableW * 0.08,
        tableW * 0.44,
        tableW * 0.12,
        tableW * 0.18,
        tableW * 0.18
      ];
  const itemRows = [
    showItemDiscount
      ? ['Sr', 'Description', 'Qty', 'Price', 'Disc.', 'Total']
      : ['Sr', 'Description', 'Qty', 'Price', 'Total']
  ];
  if (items.length === 0) {
    itemRows.push(showItemDiscount ? ['', 'No items', '', '', '', ''] : ['', 'No items', '', '', '']);
  } else {
    items.forEach((item, index) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unit_price) || 0;
      const disc = Number(item.discount) || 0;
      const lineTotal = qty * price - disc;
      if (showItemDiscount) {
        itemRows.push([
          String(index + 1),
          String(item.description || 'Item'),
          String(qty),
          money(price),
          disc > 0 ? money(disc) : '-',
          money(lineTotal)
        ]);
      } else {
        itemRows.push([
          String(index + 1),
          String(item.description || 'Item'),
          String(qty),
          money(price),
          money(lineTotal)
        ]);
      }
    });
  }

  y = drawTable(doc, {
    startX: margin,
    startY: y,
    widths: colW,
    aligns: showItemDiscount
      ? ['center', 'left', 'center', 'right', 'right', 'right']
      : ['center', 'left', 'center', 'right', 'right'],
    header: true,
    fontSize: 7.5,
    margin,
    rows: itemRows
  });
  y += 4;

  const taxAmount = Number(invoice?.tax_amount) || 0;
  let cgstAmount = Number(invoice?.cgst_amount) || 0;
  let sgstAmount = Number(invoice?.sgst_amount) || 0;
  if (taxAmount > 0 && cgstAmount <= 0 && sgstAmount <= 0) {
    cgstAmount = Math.round((taxAmount / 2) * 1000) / 1000;
    sgstAmount = Math.round((taxAmount - cgstAmount) * 1000) / 1000;
  }
  const taxRate = Number(invoice?.tax_rate) || 0;
  const halfRate = taxRate > 0 ? (taxRate / 2).toFixed(2) : '';

  const summaryRows = [['Subtotal', money(invoice?.subtotal)]];
  if (cgstAmount > 0 || sgstAmount > 0 || taxAmount > 0) {
    summaryRows.push([halfRate ? `CGST (${halfRate}%)` : 'CGST', money(cgstAmount)]);
    summaryRows.push([halfRate ? `SGST (${halfRate}%)` : 'SGST', money(sgstAmount)]);
  }
  if (Number(invoice?.discount) > 0) {
    summaryRows.push(['Discount', `-${money(invoice.discount)}`]);
  }
  summaryRows.push(['Total', money(invoice?.total_amount)]);

  const summaryW = Math.min(70, tableW * 0.55);
  const summaryX = pageW - margin - summaryW;
  y = drawTable(doc, {
    startX: summaryX,
    startY: y,
    widths: [summaryW * 0.55, summaryW * 0.45],
    aligns: ['left', 'right'],
    header: false,
    fontSize: 8,
    margin,
    rows: summaryRows
  });

  // Bold the last (Total) row text by redrawing label/value — already in table cells.
  // Emphasize Total by filling last row background was skipped to keep drawTable simple;
  // draw an extra bold overlay on the last row area is overkill. Mark via font on a note line:
  if (invoice?.notes) {
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('Notes', margin, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    const notes = doc.splitTextToSize(String(invoice.notes), pageW - margin * 2);
    doc.text(notes, margin, y);
  }

  const fileName = `${String(invoice?.invoice_number || 'invoice').replace(/[^\w.-]+/g, '_')}.pdf`;
  return { doc, fileName };
}

/**
 * Build an A5 invoice PDF blob (free to share via WhatsApp / system share).
 */
export function buildInvoicePdfBlob(invoice, customer = null) {
  const { doc, fileName } = createInvoicePdfDoc(invoice, customer);
  return { blob: doc.output('blob'), fileName };
}

/** Open the system print dialog using an A5 (148 × 210 mm) PDF. */
export function printInvoicePdf(invoice, customer = null) {
  const { doc, fileName } = createInvoicePdfDoc(invoice, customer);
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', fileName);
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:148mm;height:210mm;border:0;';
  document.body.appendChild(iframe);

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    iframe.remove();
    URL.revokeObjectURL(url);
  };

  let printed = false;
  const triggerPrint = () => {
    if (printed) return;
    printed = true;
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        console.error(err);
        window.open(url, '_blank', 'noopener');
      }
    }, 400);
  };

  iframe.onload = triggerPrint;
  iframe.src = url;
  setTimeout(triggerPrint, 1200);
  setTimeout(cleanup, 120000);
}

export async function downloadInvoicePdf(invoice, customer = null) {
  const { blob, fileName } = buildInvoicePdfBlob(invoice, customer);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return fileName;
}
